// University of Central Florida: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UCF's Common Data Set 2025-2026 (Common-Data-Set-2025-2026.pdf), linked
//    from UCF Analytics' Institutional Research Common Data Set page: C1
//    applicants, admits and enrollees for Fall 2025 with the residency
//    table, C8 test policy for Fall 2027, C21/C22 early plans, G1/G5
//    2026-2027 costs (the "not available" box is left unchecked). C8E gives
//    the score deadline as "5/1/2026", the prior cycle's date; the page
//    says May 1. G1 reports required fees as $0, so the cost table has no
//    fees row.
//  - UCF Undergraduate Admissions: Freshman Admission (Fall 2027 Early
//    Action and Regular Decision dates, what you need to apply) and the
//    Freshman FAQ (test requirement, superscoring, Early Action details).
//  - College Scorecard, school id 132903 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UCF Common Data Set 2025-2026',
        url: 'https://analytics.ucf.edu/institutional-research/common-data-set/',
        read: '2026-10-01',
    },
    freshman: {
        label: 'UCF Undergraduate Admissions, Freshman Admission',
        url: 'https://www.ucf.edu/admissions/undergraduate/freshman/',
        read: '2026-10-01',
    },
    faq: {
        label: 'UCF Undergraduate Admissions, Freshman FAQ',
        url: 'https://www.ucf.edu/admissions/undergraduate/faq/freshman/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?132903-University-of-Central-Florida',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-central-florida',
    kbId: 'university_of_central_florida',
    name: 'University of Central Florida',
    shortName: 'UCF',
    location: 'Orlando, FL',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UCF brand colors); decoration only.
    branding: {
        primary: '#000000',
        secondary: '#FFC904',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_central_florida_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 59629,
        admitted: 25522,
        enrolled: 8103,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 46049, admitted: 21823 },
            { group: 'Out-of-state', applied: 11215, admitted: 3124 },
            { group: 'International', applied: 2365, admitted: 575 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UCF\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UCF has non-binding Early Action and no early decision. The Common Data Set does not report how many applied or were admitted in Early Action, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Early Action', value: 'October 15' },
        rows: [
            { plan: 'Early Action', deadline: 'October 15', decision: 'December 4', binding: false },
            { plan: 'Regular Decision', deadline: 'May 1', decision: 'Rolling, from January', binding: false },
        ],
        note: 'For Early Action, every document is due November 15, and the December 4 decision is admit, pathway admit, defer or deny. For Regular Decision, documents are also due May 1. Meeting the Early Action dates gives you priority for merit scholarships, which usually run out by mid-January.',
        source: 'freshman',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'UCF is not test-optional: every freshman must send an official SAT, ACT or CLT score, reported directly to UCF. UCF superscores the SAT and the ACT. The ACT science section is optional; for any ACT taken in April 2025 or later, UCF builds the composite from English, math and reading.',
        source: 'faq',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for admission for fall 2027 applicants, with scores due by May 1, and says the CLT is accepted as an alternative.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshman',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [6368, 26732] },
            { item: 'Housing (on campus)', amounts: [6496, 6496] },
            { item: 'Food (on-campus meal plan)', amounts: [5940, 5940] },
            { item: 'Books and supplies', amounts: [1200, 1200] },
            { item: 'Transportation', amounts: [1094, 1094] },
            { item: 'Other expenses', amounts: [3104, 3104] },
        ],
        totals: [24202, 44566],
        note: 'For a full-time undergraduate living on campus, fall and spring. The Common Data Set lists required fees as $0.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 11516,
            byIncome: [
                { income: '$0 to $30,000', amount: 7870 },
                { income: '$30,001 to $48,000', amount: 7841 },
                { income: '$48,001 to $75,000', amount: 9881 },
                { income: '$75,001 to $110,000', amount: 13858 },
                { income: 'Over $110,000', amount: 19416 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UCF\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
